import type React from "react";

import { cn } from "@/lib/utils";

import { type VariantProps, cva } from "class-variance-authority";

const bodyTextVariants = cva("m-0 tracking-1.5 cursor-default", {
  variants: {
    size: {
      xl: "text-body-xl leading-29",
      lg: "text-body-lg leading-24",
      md: "text-body-md leading-19",
      sm: "text-body-sm leading-15",
      xs: "text-body-xs leading-12",
    },
    textColor: {
      primary: "text-primary",
      secondary: "text-secondary",
      tertiary: "text-tertiary",
      warning: "text-warning",
      info: "text-info",
      success: "text-success",
      error: "text-error",
      link: "text-link",
      white: "text-white",
    },
    bold: {
      true: "font-bold",
      false: "font-normal",
    },
    italic: {
      true: "italic",
    },
    underline: {
      true: "underline decoration-1 underline-offset-2",
    },
    lineThrough: {
      true: "line-through",
    },
    wrap: {
      false: "whitespace-nowrap", // collapses whitespace and line breaks
    },
    preserveLineBreaks: {
      true: "whitespace-pre-wrap", // preserves whitespace and line breaks
    },
    /**
     * Truncate text with ellipsis. It adds the following CSS properties:
     * overflow: hidden;
     * text-overflow: ellipsis;
     * white-space: nowrap;
     */
    truncate: {
      true: "truncate",
    },
  },
  defaultVariants: {
    size: "md",
    textColor: "primary",
    bold: false,
    italic: false,
    underline: false,
    lineThrough: false,
    truncate: false,
    preserveLineBreaks: false,
  },
});

interface BodyTextProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof bodyTextVariants> {
  tag?: "p" | "span" | "dd" | "dt" | "dl";
}

/**
 * Renders a body text component with customizable variants.
 *
 * @component
 * @param {object} props - The component props.
 * @param {string} [className] - Additional CSS class name for the component.
 * @param {string} [tag] - The HTML tag to be used for the body text.
 * @param {string} [size] - The size variant of the body text.
 * @param {string} [textColor] - The text color variant of the body text.
 * @param {boolean} [bold] - Whether the body text should be bolded.
 * @param {boolean} [italic] - Whether the body text should be italicized.
 * @param {boolean} [underline] - Whether the body text should be underlined.
 * @param {boolean} [lineThrough] - Whether the body text should have a line through it.
 * @param {boolean} [preserveLineBreaks] - Whether the body text should preserve line breaks (will also wrap the text).
 * @param {boolean} [truncate] - Whether the body text should truncate with ellipsis.
 * @param {string} [wrap] - Whether the body text should wrap or not.
 * @returns {JSX.Element} The rendered body text component.
 */
const BodyText: React.FC<BodyTextProps> = ({
  className,
  tag = "span",
  size,
  textColor,
  bold,
  italic,
  underline,
  lineThrough,
  preserveLineBreaks,
  truncate,
  wrap,
  ...props
}) => {
  const Comp = tag;
  return (
    <Comp
      className={cn(
        bodyTextVariants({
          size,
          textColor,
          bold,
          italic,
          underline,
          lineThrough,
          truncate,
          preserveLineBreaks,
          wrap,
          className,
        }),
      )}
      {...props}
    />
  );
};

export { BodyText };
